// Shared filter+search toolbar for list pages. Mirrors the pbx-portal
// pattern (see docker-pbx/portal/cloud/pages/src/pages/SubscribersList.jsx):
//
//   [search input .................. ]  [ All 12 ] [ Active 8 ] [ ... ]
//
// The `/` key from anywhere on the page focuses the search input (unless
// a text field is already focused). Filter pills carry per-key counts on
// the right side of each label so operators can see distribution at a
// glance.
//
// Props:
//   search           — current search string
//   onSearchChange   — (str) => void
//   searchPlaceholder — placeholder text ('Filter by …')
//   filters          — [{ key, label }] — the pill options
//   filterCounts     — { <key>: number } — count per filter
//   activeFilter     — current filter key
//   onFilterChange   — (key) => void
//   className        — optional extra classes on the outer div

import { useEffect, useRef } from 'react';

export default function ListToolbar({
  search, onSearchChange, searchPlaceholder = 'Filter…',
  filters, filterCounts = {}, activeFilter, onFilterChange,
  className = '',
}) {
  const inputRef = useRef(null);

  // Bind `/` to focus the search input. Ignored when the user is
  // already typing somewhere so it doesn't hijack in-form editing.
  useEffect(() => {
    function onKey(e) {
      if (e.key !== '/') return;
      const t = document.activeElement;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      e.preventDefault();
      inputRef.current?.focus();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  return (
    <div className={`flex flex-wrap items-center gap-3 ${className}`}>
      <div className="relative flex-1 min-w-[220px] max-w-lg">
        <input
          ref={inputRef}
          type="search"
          value={search ?? ''}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder={searchPlaceholder}
          className="w-full rounded-md border border-gray-300 pl-8 pr-3 py-1.5 text-sm placeholder-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
        />
        {/* Magnifying-glass glyph on the left (inline SVG so no dep). */}
        <svg
          className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400"
          viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"
        >
          <path fillRule="evenodd" d="M9 3a6 6 0 104.472 10.03l3.249 3.25a.75.75 0 101.06-1.06l-3.249-3.25A6 6 0 009 3zM4.5 9a4.5 4.5 0 119 0 4.5 4.5 0 01-9 0z" clipRule="evenodd" />
        </svg>
        <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 hidden rounded border border-gray-200 bg-gray-50 px-1 text-[10px] font-mono text-gray-400 sm:inline">/</span>
      </div>

      {filters && filters.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {filters.map((f) => {
            const isActive = activeFilter === f.key;
            const count = filterCounts[f.key];
            return (
              <button
                key={f.key}
                type="button"
                onClick={() => onFilterChange(f.key)}
                className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                  isActive
                    ? 'border-blue-600 bg-blue-50 text-blue-700'
                    : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                {f.label}
                {typeof count === 'number' && (
                  <span className={`rounded-full px-1.5 text-[10px] font-semibold ${
                    isActive ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'
                  }`}>
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
